import { useCallback, useEffect, useRef, useState } from "react";

export interface ScrollFadeEdges {
  readonly start: boolean;
  readonly end: boolean;
}

/**
 * Tracks whether a scroll container has content clipped beyond its start or
 * end edge, so a strip can signal overflow (edge fades) instead of hiding it
 * silently. `axis` picks the scroll direction; RTL negative scrollLeft is
 * normalized. Re-measures after every render (content changes re-render the
 * owner) and on container resize; environments without ResizeObserver (jsdom)
 * still get the render-time measurement.
 */
export function useScrollFade<T extends HTMLElement>(axis: "x" | "y" = "x") {
  const ref = useRef<T | null>(null);
  const [edges, setEdges] = useState<ScrollFadeEdges>({ start: false, end: false });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const scrolled = Math.abs(axis === "x" ? el.scrollLeft : el.scrollTop);
    const visible = axis === "x" ? el.clientWidth : el.clientHeight;
    const total = axis === "x" ? el.scrollWidth : el.scrollHeight;
    setEdges((previous) => {
      const next = {
        start: scrolled > 1,
        end: Math.ceil(scrolled + visible) < total - 1,
      };
      return next.start === previous.start && next.end === previous.end ? previous : next;
    });
  }, [axis]);

  useEffect(update);

  useEffect(() => {
    const el = ref.current;
    const view = el?.ownerDocument.defaultView;
    if (!el || !view?.ResizeObserver) return;
    const observer = new view.ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [update]);

  return { ref, edges, onScroll: update };
}
